import { DropAreaVMConfig } from 'components/droparea/droparea';
import { ChatEditorVMConfig, IChatEditorVM } from 'components/editors/chat/chat.interfaces';
import { EmojisTool } from 'components/editors/chat/tools/emojis/emojis';
import { Tool } from 'components/editors/chat/tools/tool';
import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Observable, Subscribable, SubscribableOrNullableValue } from 'knockout';
import TopicMediaManager from 'managers/topic/media';
import Quill from 'quill';
import TextThreadEditorVM from './textThread';
import './textThreadBody.html';
import './textThreadBody.scss';

const Keyboard = Quill.import('modules/keyboard');

components.preload('chat-editor', 'wcc-droparea', 'thread-media');

interface TextThreadEditorBodyVMConfig {
    editor: TextThreadEditorVM,
    mediaSize: SubscribableOrNullableValue<number>
    emojis: SubscribableOrNullableValue<boolean>
}

@injectable()
export default class TextThreadEditorBodyVM {
    private editor: TextThreadEditorVM

    id = _.uniqueId('text-thread-editor-body-')
    labels = labels

    errorsShown: Observable<boolean>

    titleEditorConfig: ChatEditorVMConfig
    titleEditor: Observable<IChatEditorVM | undefined>
    title: Observable<string>

    textEditorConfig: ChatEditorVMConfig
    textEditor: Observable<IChatEditorVM | undefined>
    text: Observable<string>

    imageDroparea: DropAreaVMConfig
    filesDroparea: DropAreaVMConfig
    videoDroparea: DropAreaVMConfig

    mediaManager: TopicMediaManager
    mediaSize: SubscribableOrNullableValue<number>
    hasMedia: Subscribable<boolean>

    textIsAllowed: Subscribable<boolean>

    textMode: Subscribable<boolean>
    imagesMode: Subscribable<boolean>
    filesMode: Subscribable<boolean>
    videoMode: Subscribable<boolean>

    isTitleEditorNeeded: Subscribable<boolean>
    isCaptionEditorNeeded: Subscribable<boolean>
    isVoxPopMessageNeeded: Subscribable<boolean>
    isMaterialUIShowing: Subscribable<boolean>

    isImageDropareaShown = ko.pureComputed(() => this.imagesMode());
    isFilesDropareaShown = ko.pureComputed(() => this.filesMode());
    isVideoDropareaShown = ko.pureComputed(() => this.videoMode() && !this.hasMedia());
    isDropAreaShown = ko.pureComputed(() => this.isImageDropareaShown() || this.isFilesDropareaShown() || this.isVideoDropareaShown());

    constructor(
        @inject(wccModules.componentConfig) config: TextThreadEditorBodyVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo
    ) {
        const emojisAllowed = ko.flattenComputed(config.emojis, true);
        this.mediaSize = config.mediaSize;

        this.editor = config.editor;
        this.errorsShown = this.editor.errorsShown;

        this.textIsAllowed = this.editor.textIsAllowed;

        this.textMode = this.editor.textMode;
        this.imagesMode = this.editor.imagesMode;
        this.filesMode = this.editor.filesMode;
        this.videoMode = this.editor.videoMode;

        this.isTitleEditorNeeded = this.editor.isTitleEditorNeeded;
        this.isCaptionEditorNeeded = this.editor.isCaptionEditorNeeded;
        this.isVoxPopMessageNeeded = this.editor.isVoxPopMessageNeeded;
        this.isMaterialUIShowing = this.editor.isMaterialUIShowing;

        this.titleEditor = this.editor.titleEditor;
        this.title = this.editor.title;

        const titleEditorPlaceHolder = this.editor.titlePlaceholder;
        const titleEditorFocusNeeded = ko.pureComputed(() => this.isTitleEditorNeeded() && this.editor.isFocusedOnRender());

        this.titleEditorConfig = {
            placeholder: titleEditorPlaceHolder,
            focus: titleEditorFocusNeeded,
            formats: [],
            noBorder: true,

            bindings: {
                'tab': { key: Keyboard.keys.TAB, handler: () => true }
            }
        }

        this.textEditor = this.editor.textEditor;
        this.text = this.editor.text;

        const textEditorPlaceholder = this.editor.textPlaceholder;
        const textEditorFocusNeeded = ko.pureComputed(() => !this.isTitleEditorNeeded() && this.editor.isFocusedOnRender());

        this.textEditorConfig = {
            placeholder: textEditorPlaceholder,
            focus: textEditorFocusNeeded,
            background: false,
            noBorder: true,

            formats: ['wcc-emoji'],

            bindings: {
                //'handleShiftEnter': { key: Keyboard.keys.ENTER, shiftKey: true, handler: () => this.editor.save() },
                'tab': { key: Keyboard.keys.TAB, handler: () => true }
            },

            toolbar: {
                right: [
                    {
                        tools: editor => {
                            const tools: Array<Tool> = [];

                            if (emojisAllowed())
                                tools.push(new EmojisTool({ icon: 'far fa-smile', pick: editor.insertEmoji }));

                            return tools;
                        },

                        isPinned: true
                    }
                ],

                placement: 'top'
            }
        }

        this.mediaManager = this.editor.attachmentsManager;
        this.hasMedia = this.mediaManager.media.any();

        this.imageDroparea = {
            onDrop: file => this.editor.uploadImage(file)
        }

        this.filesDroparea = {
            onDrop: file => this.editor.uploadFile(file)
        }

        this.videoDroparea = {
            onDrop: file => this.editor.uploadVideo(file),
            disabled: !settings.proxyTaskResources
        }

        ko.applyBindingsToNode(info.element, {
            'click': () => this.onBodyClick(),

            'css': {
                'thread-editor-text-body': true
            }
        }, this);
    }

    pickImage = () => {
        this.editor.pickImage();
    }

    pickFiles = () => {
        this.editor.pickFiles();
    }

    pickVideo = () => {
        this.editor.pickVideo();
    }

    forceTextMode = () => {
        this.editor.isForcedTextMode(true);
    }

    private onBodyClick() {
        const hasFocus = this.titleEditor()?.hasFocus() || this.textEditor()?.hasFocus() || false;

        if (!hasFocus) {
            if (this.isTitleEditorNeeded())
                this.titleEditor()?.focus();
            else
                this.textEditor()?.focus();
        }
    }
}